import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, NavLink } from 'react-router-dom';
import { supabase } from './supabaseClient';
import HomePage from './components/HomePage';
import SubmitForm from './components/SubmitForm';
import CompareProfiles from './components/CompareProfiles';
import Leaderboard from './components/Leaderboard';
import AuthComponent from './components/AuthComponent';
import AccountPage from './components/AccountPage';
import SubmitSuccess from './components/SubmitSuccess';
import ResetPassword from './components/ResetPassword';
import PurchaseUnlocks from './components/PurchaseUnlocks';
import UnlockedProfiles from './components/UnlockedProfiles';
import FullProfileView from './components/FullProfileView';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Component to show pages with login prompt
const PageWithLoginPrompt = ({ children, feature }) => {
  return (
    <div className="page-with-login-prompt">
      <div className="login-prompt-banner">
        <div className="login-prompt-content">
          <h3>Please log in to {feature}</h3>
          <p>Create an account or sign in to access this feature and compete with other students.</p>
          <Link to="/login" className="login-prompt-button">
            Login / Sign Up
          </Link>
        </div>
      </div>
      <div className="page-content-disabled">
        {children}
      </div>
    </div>
  );
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });
  const [userHasProfile, setUserHasProfile] = useState(false);

  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setLoading(false);
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    async function checkUserProfile() {
      if (session && session.user) {
        try {
          // Get user data to check submitted_profile
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('submitted_profile')
            .eq('id', session.user.id)
            .single();
          
          // Also check if profile actually exists in profiles table
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id')
            .eq('user_id', session.user.id);
          
          if (profileError) {
            console.error('Error checking profile:', profileError);
          } else {
            const hasProfile = profileData && profileData.length > 0;
            console.log("Profile exists check:", hasProfile);
            
            // If there's a mismatch, try to fix it
            if (hasProfile && userData && !userData.submitted_profile) {
              console.log("Detected mismatch - fixing submission status...");
              const { error: updateError } = await supabase
                .from('users')
                .update({ submitted_profile: true })
                .eq('id', session.user.id);
                
              if (updateError) {
                console.error("Failed to update submission status:", updateError);
              } else {
                console.log("Successfully fixed submission status");
                userData.submitted_profile = true;
              }
            }
          }
          
          // Update state with either fixed or original value
          if (!userError && userData) {
            setUserHasProfile(userData.submitted_profile);
          }
        } catch (err) {
          console.error('Error in profile status check:', err);
        }
      } else {
        setUserHasProfile(false);
      }
    }
    
    checkUserProfile();
  }, [session]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div className={`App ${theme}`}>
        <nav className="App-nav">
          <Link to="/" className="nav-logo college-arena-brand">College Arena</Link>
          <div className="nav-links">
            <NavLink 
              to="/" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Home
            </NavLink>
            <NavLink 
              to="/leaderboard" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Leaderboard
            </NavLink>
            <NavLink 
              to="/submit" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Submit Profile
            </NavLink>
            <NavLink 
              to="/compare" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Compare Profiles
            </NavLink>
            <NavLink 
              to="/purchase-unlocks" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Buy Unlocks
            </NavLink>
            <NavLink 
              to="/unlocked-profiles" 
              end
              className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
            >
              Unlocked Profiles
            </NavLink>
            
            <div className="nav-auth-separator"></div>

            {!session ? (
              <NavLink 
                to="/login" 
                end
                className={({ isActive }) => "nav-link auth-link" + (isActive ? " active" : "")}
              >
                Login / Sign Up
              </NavLink>
            ) : (
              <>
                <NavLink 
                  to="/account" 
                  end
                  className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
                >
                  Account
                </NavLink>
                <button onClick={async () => {
                  await supabase.auth.signOut();
                }} className="nav-link auth-link logout-button">Logout</button>
              </>
            )}
            
            <button onClick={toggleTheme} className="theme-toggle-button">
              <div className={`icon-container ${theme}`}>
                <div className="sun"></div>
                <div className="moon"></div>
              </div>
            </button>
          </div>
        </nav>

        <main className="App-main">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route 
              path="/submit" 
              element={
                session ? (
                  <SubmitForm />
                ) : (
                  <PageWithLoginPrompt feature="submit your profile">
                    <SubmitForm />
                  </PageWithLoginPrompt>
                )
              } 
            />
            <Route path="/submit-success" element={<SubmitSuccess />} />
            <Route 
              path="/compare" 
              element={
                session ? (
                  <CompareProfiles />
                ) : (
                  <PageWithLoginPrompt feature="compare profiles">
                    <CompareProfiles />
                  </PageWithLoginPrompt>
                )
              } 
            />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/login" element={!session ? <AuthComponent /> : <Navigate to="/account" />} />
            <Route path="/signup" element={!session ? <Navigate to="/login?signup=true" replace state={{ signup: true }} /> : <Navigate to="/account" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/purchase-unlocks" 
              element={<PurchaseUnlocks />} 
            />
            <Route 
              path="/unlocked-profiles" 
              element={<UnlockedProfiles />} 
            />
            <Route 
              path="/profile/:id" 
              element={
                session ? (
                  <FullProfileView />
                ) : (
                  <PageWithLoginPrompt feature="view profiles">
                    <FullProfileView />
                  </PageWithLoginPrompt>
                )
              } 
            />
          </Routes>
        </main>

        <footer className="App-footer">
          <p>&copy; {new Date().getFullYear()} College Profile Ranker</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
